/**
 * @file SeroStatusHepatitisBListView.jsx
 * @author Daniel Aguirre
 * @description Sero Status Hepatitis B List View Page
 */

// Dependencies
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';

// Helpers
import {
  joinArrayObject,
  removeNullValuesFromObject,
  sortStringPlus
} from 'commons/helpers/utils';
import {
  createColumns,
  PAGE_LIMIT,
  getSearchApiKeys,
  getSorterString,
  getSelectFilter
} from '../helper/utils';
import DashboardDetailList from '../dashboardDetailList';

function SeroStatusHepatitisBListView({ dashboardStaffActions, clinicId }) {
  const { t: translate } = useTranslation();
  const [rowData, setRowData] = React.useState([]);
  const { classification, totalItems: totalItemsParams } = useParams();
  const [totalItems, setTotalItems] = useState(totalItemsParams);
  const [dataFilter, setDataFilter] = useState(null);
  const [columns, setColumns] = useState([]);
  const [filter, setFilter] = useState(null);
  const [sorter, setSorter] = useState(null);

  const { getSeroStatusHepatitisBList } = dashboardStaffActions;

  const additionalColumns = [
    {
      title: translate('dashboardStaff.vaccinationSchedule'),
      dataIndex: 'esquema_vacunacion',
      filter: {
        listName: 'listaEsquemaVacunacion',
        searchName: 'filterEsquemaVacunacion',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('esquema_vacunacion'),
        multiple: 7
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'listaEsquemaVacunacion',
            searchName: 'filterEsquemaVacunacion'
          },
          dataIndex: 'esquema_vacunacion'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.treatmentOptions'),
      dataIndex: 'opciones_tratamiento',
      filter: {
        listName: 'listaOpcionesTratamiento',
        searchName: 'filterOpcionesTratamiento',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('opciones_tratamiento'),
        multiple: 7
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'listaOpcionesTratamiento',
            searchName: 'filterOpcionesTratamiento'
          },
          dataIndex: 'opciones_tratamiento'
        },
        dataFilter
      ),
      ellipsis: true
    }
  ];

  const handleTableChange = (pagination, filters, sorterFilter) => {
    if (filters || sorterFilter) {
      const convertedFilters = getSearchApiKeys(
        [...columns, ...additionalColumns],
        removeNullValuesFromObject(filters)
      );
      const convertedSorts = getSorterString(sorterFilter);
      setFilter(convertedFilters);
      setSorter(convertedSorts);
    }
  };

  const getetData = (offset, pageLimit) => {
    const pageSize = pageLimit || PAGE_LIMIT;
    const joinFilter = joinArrayObject(filter);
    const joinSorter = joinArrayObject(sorter);
    const data = {
      params: {
        idClinic: clinicId,
        filterClasificacionSeleccionada: classification,
        limit: pageSize,
        offset,
        ...joinFilter,
        ...joinSorter
      }
    };
    getSeroStatusHepatitisBList(data, (_, resp) => {
      if (resp?.data && resp.data?.totalRegistros != null) {
        setTotalItems(resp.data?.totalRegistros);
        setRowData(resp.data?.listaDetalles ?? []);
        setDataFilter(resp.data);
      }
    });
  };

  useEffect(() => {
    if (clinicId !== null) getetData(1);
  }, [clinicId]);

  useEffect(() => {
    const cols = createColumns(translate, dataFilter);
    setColumns(cols);
  }, [rowData, dataFilter]);

  return (
    <DashboardDetailList
      columns={columns}
      rowData={rowData}
      totalItems={totalItems}
      onPaginationChange={getetData}
      additionalColumns={additionalColumns}
      onChange={handleTableChange}
      filter={filter}
      sorter={sorter}
    />
  );
}

/**
 * Component definition of proptypes.
 */
SeroStatusHepatitisBListView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getSeroStatusHepatitisBList: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default SeroStatusHepatitisBListView;
